import React, { useState, useEffect, useContext } from "react";
import { Table, Spin, Tooltip, Checkbox, Tag } from "antd";
import { EditTwoTone, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import Link from "antd/es/typography/Link";
import { adminContext } from "../../Common/PageRoute";
import { PageLayout, PageHeader, SectionCard, TableToolbar } from "../../Common/Layout";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import TicketAlertTemplatesService from "../../Services/TicketAlertTemplatesService";
import TicketAlertTemplatesDetailsPanel from "./TicketAlertTemplatesDetailsPanel";

interface TicketAlertTemplateItem {
  id: string;
  sno: number;
  name: string;
  subject: string;
  isActive: boolean;
  isInsertInContent: boolean;
  teamName: string;
  attachmentCount: number;
  notification?: {
    isEnabled?: boolean;
    alertEventKey?: string;
    alertEventName?: string;
    isExcluded?: boolean;
  };
}

interface TicketAlertTemplatesListState {
  items: TicketAlertTemplateItem[];
  totalItems: number;
}

const TicketAlertTemplatesList: React.FC = () => {
  const { openNotification } = useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dataList, setDataList] = useState<TicketAlertTemplatesListState>({
    items: [],
    totalItems: 0,
  });

  const [dialog, setDialog] = useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });

  const [listParams, setListParams] = useState({
    search: "",
    status: "isActive", // default to active messages
    sortDir: "ascend",
    sortCol: "Name",
    currentPage: 1,
    pageSize: 15,
  });

  const fetchItems = async (start: number, length: number) => {
    setLoading(true);
    try {
      const res = await TicketAlertTemplatesService.getList(
        start,
        length,
        listParams.search,
        listParams.sortCol,
        listParams.sortDir === "ascend" ? "asc" : "desc",
        listParams.status
      );

      if (res?.status && res?.result) {
        setDataList({
          totalItems: res.result.totalRecords ?? 0,
          items: res.result.items ?? [],
        });
      } else {
        openNotification("error", res?.message || "Failed to fetch alert templates");
      }
    } catch (err) {
      openNotification("error", "Error fetching alert templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(
      (listParams.currentPage - 1) * listParams.pageSize,
      listParams.pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, refresh]);

  const handleToggle = async (id: string, field: string, isTrue: boolean, name: string) => {
    try {
      const res = await TicketAlertTemplatesService.toggleCannedMessage(id, field, isTrue);
      if (res?.status) {
        openNotification(
          "success",
          `Alert template "${name}" ${field === "isActive" ? (isTrue ? "activated" : "deactivated") : (isTrue ? "allowed in-content" : "disallowed in-content")} successfully.`
        );
        setRefresh(!refresh);
      } else {
        openNotification("error", res?.message || "Failed to update alert template status");
      }
    } catch (err) {
      openNotification("error", "Error updating alert template status");
    }
  };

  const columns: ColumnsType<TicketAlertTemplateItem> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "8%",
      render: (_, __, index) => (
        <p>
          {listParams.currentPage > 1
            ? listParams.pageSize * (listParams.currentPage - 1) + index + 1
            : index + 1}
        </p>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "Name",
      width: "25%",
      sorter: true,
      showSorterTooltip: false,
      render: (_, record) => <span>{record.name}</span>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "Subject",
      width: "30%",
      sorter: true,
      showSorterTooltip: false,
      render: (_, record) => <span>{record.subject || "-"}</span>,
    },
    {
      title: "Attachments",
      dataIndex: "attachmentCount",
      key: "AttachmentCount",
      width: "12%",
      render: (_, record) => <span>{record.attachmentCount ?? 0}</span>,
    },
    {
      title: "Alert event",
      dataIndex: "notification",
      key: "Notification.AlertEventName",
      width: "20%",
      render: (_, record) => {
        if (!record.notification?.isEnabled) return <span>-</span>;
        return (
          <Tooltip title={record.notification.alertEventName || ""}>
            <Tag 
              color={record.notification.isExcluded ? "default" : "blue"}
              style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
            >
              {record.notification.alertEventName || record.notification.alertEventKey}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "IsActive",
      width: "8%",
      render: (_, record) => (
        <Tooltip title={record.isActive ? "Deactivate" : "Activate"}>
          <Checkbox
            checked={record.isActive}
            onChange={(e) => {
              setDialog({
                type: "toggleActive",
                params: {
                  id: record.id,
                  name: record.name,
                  targetValue: e.target.checked,
                },
              });
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "8%",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Tooltip title="Edit" placement="top">
            <Link
              className="edit-btn"
              onClick={() => {
                setDialog({ type: "editEmail", params: { record } });
              }}
            >
              <EditTwoTone />
            </Link>
          </Tooltip>
        </div>
      ),
    },
  ];

  const primaryActions: any[] = [
    {
      label: "Template",
      icon: <PlusOutlined />,
      type: "primary",
      onClick: () => setDialog({ type: "editEmail", params: {} }),
    },
  ];


  const filterCriteria = [
    {
      key: "status",
      label: "Status",
      type: "select" as const,
      options: [
        { value: "", label: "All" },
        { value: "isActive", label: "Active" },
        { value: "isInActive", label: "Inactive" },
      ],
      placeholder: "Select status",
    },
  ];

  const activeFilters = {
    status: listParams.status,
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setListParams({
      ...listParams,
      status: newFilters.status,
      currentPage: 1,
    });
  };

  return (
    <PageLayout>
      <PageHeader title="Ticket alert templates" subtitle="Manage notification templates for ticket events" />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={(val: string) => setListParams({ ...listParams, search: val, currentPage: 1 })}
            searchPlaceholder="Search alert templates..."
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            onRefresh={() => setRefresh(!refresh)}
          />
        </div>
        <Table
          dataSource={dataList.items}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: window.innerHeight - 256 }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            current: listParams.currentPage,
            pageSize: listParams.pageSize,
            showSizeChanger: true,
            total: dataList.totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging, filter, sort: any) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setListParams({
                ...listParams,
                sortDir: sort.order !== undefined ? sort.order : "ascend",
                sortCol: sort.order !== undefined ? sort.columnKey : "Name",
                currentPage: paging.current,
                pageSize: paging.pageSize,
              });
            }
          }}
        />

        <TicketAlertTemplatesDetailsPanel
          visible={dialog.type === "editEmail"}
          record={dialog.params?.record}
          onClose={(confirmed: boolean) => {
            setDialog({ type: "", params: {} });
            if (confirmed) {
              setRefresh(!refresh);
            }
          }}
        />

        {dialog.type === "toggleActive" && (
          <ConfirmationModal
            open={true}
            onNo={() => setDialog({ type: "", params: {} })}
            onYes={async () => {
              const { id, name, targetValue } = dialog.params;
              await handleToggle(id, "isActive", targetValue, name);
              setDialog({ type: "", params: {} });
            }}
            text={`Are you sure you want to ${
              dialog.params.targetValue ? "activate" : "deactivate"
            } the alert template "${dialog.params.name}"?`}
          />
        )}
      </SectionCard>
    </PageLayout>
  );
};

export default TicketAlertTemplatesList;
